import React, { useState, useEffect, useMemo } from 'react';
import './style.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosInstance';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import Typography from '../common/StyledTypography';
import { Form, withFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormikPasswordInput } from '../common/FormikComponent';
import Info from 'apollo-react-icons/Info';
import { resetPasswordSchema } from '../../validation/auth';
import { toast } from 'react-toastify';
import AuthLayout from './AuthLayout';

const initialValues = {
  newPassword: '',
  confirmPassword: '',
};

const onFormSubmit = async (
  values,
  { setSubmitting, setFieldError, props }
) => {
  try {
    const response = await axios.post('/api/v1/auth/reset-password', {
      token: props.token,
      newPassword: values.newPassword,
    });

    if (response.data.success) {
      toast.success('Password changed successfully');
      setTimeout(() => {
        props.navigate('/login');
      }, 1000);
    } else {
      toast.error(response.data.message || 'Failed to reset password');
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || 'Failed to reset password';
    toast.error(errorMessage);
    setFieldError('newPassword', errorMessage);
  }
  setSubmitting(false);
};

const NewPassword = (props) => {
  const { token, navigate, isSubmitting, handleSubmit } = props;
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    const verifyTokenEffect = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/v1/auth/verify-reset-token/${token}`
        );

        if (response.data.success) {
          setTokenValid(true);
        } else {
          throw new Error(
            response.data.message || 'Invalid or expired reset link'
          );
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setTokenValid(false);
        const errorMessage = error.message || 'Error verifying reset link';
        toast.error(errorMessage);
        setTimeout(() => {
          navigate('/forgot-password');
        }, 1000);
      } finally {
        setLoading(false);
      }
    };

    verifyTokenEffect();
  }, [token, navigate]);

  if (loading || tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Typography variant="bodySmall" style={{ margin: '8px 0' }}>
            Verifying reset link...
          </Typography>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return null;
  }

  return (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2 className="signInTitle">Set New Password</h2>
            <p className="signInDesc">
              Enter your new password below. Make sure it&apos;s strong and
              secure.
            </p>
          </div>
        </Grid>

        <Grid container className="formContainer">
          <Grid container justifyContent="center" sx={{ width: '100%' }}>
            <Grid size={12}>
              <Form noValidate onSubmit={handleSubmit}>
                <FormikPasswordInput
                  name="newPassword"
                  key="newPassword"
                  label={
                    <span className="formLabelWithIcon">
                      New Password
                      <Info
                        fontSize="extraSmall"
                        style={{ color: '#595959' }}
                      />
                    </span>
                  }
                  fullWidth
                  disabled={isSubmitting}
                />
                <FormikPasswordInput
                  name="confirmPassword"
                  key="confirmPassword"
                  label={
                    <span className="formLabelWithIcon">
                      Confirm Password
                      <Info
                        fontSize="extraSmall"
                        style={{ color: '#595959' }}
                      />
                    </span>
                  }
                  fullWidth
                  disabled={isSubmitting}
                />

                <Button
                  variant="primary"
                  className="loginButton"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </Form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

// Create FormikNewPassword with translation support
const createFormikNewPassword = (t) =>
  withFormik({
    validationSchema: toFormikValidationSchema(resetPasswordSchema(t)),
    mapPropsToValues: () => initialValues,
    handleSubmit: onFormSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  })(NewPassword);

export default function NewPasswordWithNavigate(props) {
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useTranslation();

  // Memoize FormikNewPassword to avoid recreating HOC on every render
  const FormikNewPassword = useMemo(() => createFormikNewPassword(t), [t]);

  return <FormikNewPassword {...props} navigate={navigate} token={token} />;
}
